import { payeeSuggestionsApi, type ForgottenPayeeSuggestion } from '@/api/payeeSuggestions';

/**
 * TMMAPP-0139 item 9 (TMMAPI-0176): a forgotten page reads at most `limit` rows and serves only some
 * of them, so a page can come back empty while `nextCursor` says there is more. These read on past
 * empty pages, a bounded number of times, and never decide "none" while a cursor remains.
 */

/** Pages read past empty ones before stopping (the caller keeps the cursor). */
export const FORGOTTEN_PAGE_BOUND = 5;

export interface ForgottenRead {
  items: ForgottenPayeeSuggestion[];
  nextCursor: string | null;
}

/** From `cursor`, the first page that holds an item, or the end, or the bound with its cursor. */
export async function readForgotten(cursor: string | null, limit: number): Promise<ForgottenRead> {
  let at = cursor;
  for (let read = 0; read < FORGOTTEN_PAGE_BOUND; read += 1) {
    const page = await payeeSuggestionsApi.forgotten(at, limit);
    if (page.items.length > 0 || (page.nextCursor ?? null) === null) {
      return { items: page.items, nextCursor: page.nextCursor ?? null };
    }
    at = page.nextCursor;
  }
  return { items: [], nextCursor: at };
}

/** Whether anything is forgotten: an item, or a cursor still left after the bound. */
export async function hasForgottenSuggestions(): Promise<boolean> {
  const read = await readForgotten(null, 1);
  return read.items.length > 0 || read.nextCursor !== null;
}
